import React from "react";
import moment from "moment";
import { CSVLink } from "react-csv";

import APIComponent from "./API";
import core from "../vendors/core-api";
import {
  getStatusDisplay,
  Humanize,
  decryptPaymentDetails,
  normalizeText,
} from "../../app/helpers";

export const csvHeadersByOrder = [
  { label: "Order Details", key: "transaction_number" },
  { label: "Order Date", key: "order_date" },
  { label: "Order Time", key: "order_time" },
  { label: "Store", key: "store_name" },
  { label: "Order Type", key: "order_type" },
  { label: "Delivery Partner", key: "delivery_partner" },
  { label: "Status", key: "status" },

  { label: "Customer Name", key: "customer_name" },
  { label: "Customer Email", key: "email" },
  { label: "Contact Number", key: "contact_number" },
  { label: "Delivery Address", key: "delivery_address" },
  { label: "City", key: "city" },
  { label: "Customer Remarks", key: "customer_remarks" },

  { label: "Total Items Count", key: "total_items_count" },
  { label: "Total Quantity", key: "total_quantity" },
  { label: "Items(Extras)", key: "items" },

  { label: "Payment ID", key: "payment_details" },
  { label: "Payment Method", key: "payment_method" },
  { label: "Delivery Charge", key: "chargedDeliveryFee" },
  { label: "Order Total Amount", key: "amount" },
  { label: "Order Promo Discount", key: "promo_discount" },

  { label: "Activity Timeline", key: "activity_timeline" },
  { label: "Timestamp - New", key: "time_new" },
  { label: "Timestamp - Accepted", key: "time_accepted" },
  { label: "Timestamp - Ready", key: "time_ready" },
  { label: "Timestamp - Out for Delivery", key: "time_out_for_delivery" },
  { label: "Timestamp - Completed", key: "time_completed" },
  { label: "Timestamp - Cancelled", key: "time_cancelled" },
  { label: "Timestamp - Refunded", key: "time_refunded" },
];

export const csvHeadersByItem = [
  { label: "Order Details", key: "transaction_number" },
  { label: "Order Date", key: "order_date" },
  { label: "Order Time", key: "order_time" },
  { label: "Store", key: "store_name" },
  { label: "Order Type", key: "order_type" },
  { label: "Delivery Partner", key: "delivery_partner" },
  { label: "Status", key: "status" },

  { label: "Customer Name", key: "customer_name" },
  { label: "Customer Email", key: "email" },
  { label: "Contact Number", key: "contact_number" },
  { label: "Delivery Address", key: "delivery_address" },
  { label: "City", key: "city" },
  { label: "Customer Remarks", key: "customer_remarks" },

  { label: "Item / Product Name", key: "item_name" },
  { label: "Item Code / SKU", key: "item_code" },
  { label: "Item Extras", key: "item_extras" },
  { label: "Quantity", key: "qty" },
  { label: "Unit Price", key: "unit_price" },
  { label: "Regular Price", key: "regular_price" },
  { label: "Line Total", key: "line_amount" },
  { label: "Item Discount", key: "item_discount" },
  { label: "Order Promo Discount", key: "promo_discount" },

  { label: "Payment ID", key: "payment_details" },
  { label: "Payment Method", key: "payment_method" },
  { label: "Delivery Charge", key: "chargedDeliveryFee" },
  { label: "Order Total Amount", key: "amount" },
  { label: "Items(Extras)", key: "items" },

  { label: "Activity Timeline", key: "activity_timeline" },
  { label: "Timestamp - New", key: "time_new" },
  { label: "Timestamp - Accepted", key: "time_accepted" },
  { label: "Timestamp - Ready", key: "time_ready" },
  { label: "Timestamp - Out for Delivery", key: "time_out_for_delivery" },
  { label: "Timestamp - Completed", key: "time_completed" },
  { label: "Timestamp - Cancelled", key: "time_cancelled" },
  { label: "Timestamp - Refunded", key: "time_refunded" },
];

export const csvHeaders = csvHeadersByItem;

const formatTimestamp = (ts) => {
  if (!ts) return "";
  const num = Number(ts);
  const m = !isNaN(num)
    ? num < 1e11
      ? moment.unix(num)
      : moment(num)
    : moment(ts);
  return m.isValid() ? m.format("YYYY-MM-DD HH:mm:ss") : String(ts);
};

const getExportFilename = (baseFilename = "export.csv", mode = "order") => {
  if (!baseFilename) return `export-by-${mode}.csv`;
  const clean = baseFilename.endsWith(".csv")
    ? baseFilename.slice(0, -4)
    : baseFilename;
  return `${clean}-by-${mode}.csv`;
};

class ExportCSV extends APIComponent {
  constructor(props) {
    super(props);

    this.state = {
      data: [],
      headers: props.headers || csvHeadersByOrder,
      activeFilename: props.filename || "export.csv",
      activeMode: "order",
    };

    this.csvLinkEl = React.createRef();
    this.data_url = props.url;
    this.api = core("pu").get();
    this.initialLoad = true;
    this.csvHeaders = props.headers || csvHeadersByOrder;
  }

  async getData() {}

  async getCSVData(e, mode = "order") {
    if (e && e.stopPropagation) e.stopPropagation();

    this.initialLoad = false;
    this._isMounted &&
      this.setState({ data_status: "fetching", activeMode: mode });

    this.api &&
      this.api
        .post({
          url: this.props.url,
          data: { ...this.props.reqBody, isForExport: true },
        })
        .then((data) => {
          const orders = data?.data?.orders || [];
          const rows = [];
          const isItemMode = mode === "item";
          const activeHeaders =
            this.props.headers ||
            (isItemMode ? csvHeadersByItem : csvHeadersByOrder);
          const activeFilename = getExportFilename(this.props.filename, mode);

          orders.forEach((order) => {
            const paymentDetails = decryptPaymentDetails(order.payment_details);
            const orderType = Humanize(order.order_type || "");
            const status = getStatusDisplay(order.status, order.order_type);
            const city =
              order.order_type === "delivery" && order.city
                ? `${order.city}`
                : "-";
            const deliveryAddress =
              order.order_type === "delivery" && order.delivery_address
                ? `${normalizeText(
                    order.delivery_address.replace(/['"]+/g, ""),
                  )}`
                : "-";
            const storeName = order.store_name || "-";
            const deliveryPartner = order.delivery_partner
              ? Humanize(order.delivery_partner)
              : "-";
            const customerRemarks =
              order.customer_remarks || order.notes || "-";
            const promoDiscount =
              order.promo_discount || order.promoDiscount || 0;
            const orderTime = order.order_time || "";

            // Parse status timestamps
            let parsedTimestamps = {};
            if (order.change_timestamp) {
              try {
                parsedTimestamps =
                  typeof order.change_timestamp === "string"
                    ? JSON.parse(order.change_timestamp)
                    : order.change_timestamp;
              } catch {
                parsedTimestamps = {};
              }
            }

            const activityTimeline =
              Object.entries(parsedTimestamps)
                .filter(([, v]) => v)
                .sort((a, b) => Number(a[1]) - Number(b[1]))
                .map(
                  ([k, v]) => `${getStatusDisplay(k)}: ${formatTimestamp(v)}`,
                )
                .join(" | ") || "-";

            const timeNew = formatTimestamp(parsedTimestamps.new);
            const timeAccepted = formatTimestamp(parsedTimestamps.accepted);
            const timeReady = formatTimestamp(parsedTimestamps.ready);
            const timeOutForDelivery = formatTimestamp(
              parsedTimestamps.out_for_delivery,
            );
            const timeCompleted = formatTimestamp(
              parsedTimestamps.completed ||
                parsedTimestamps.completed_full_refund ||
                parsedTimestamps.completed_partial_refund,
            );
            const timeCancelled = formatTimestamp(
              parsedTimestamps.cancelled ||
                parsedTimestamps.cancelled_full_refund ||
                parsedTimestamps.cancelled_partial_refund ||
                parsedTimestamps.cancelled_no_refund ||
                parsedTimestamps.cancelled_no_show ||
                parsedTimestamps.cancelled_abandoned,
            );
            const timeRefunded = formatTimestamp(parsedTimestamps.refunded);

            const baseRow = {
              ...order,
              payment_details: paymentDetails,
              order_type: orderType,
              status,
              city,
              delivery_address: deliveryAddress,
              store_name: storeName,
              delivery_partner: deliveryPartner,
              customer_remarks: customerRemarks,
              promo_discount: promoDiscount,
              order_time: orderTime,
              activity_timeline: activityTimeline,
              time_new: timeNew,
              time_accepted: timeAccepted,
              time_ready: timeReady,
              time_out_for_delivery: timeOutForDelivery,
              time_completed: timeCompleted,
              time_cancelled: timeCancelled,
              time_refunded: timeRefunded,
            };

            let details = order.order_details;
            if (typeof details === "string") {
              try {
                details = JSON.parse(details);
              } catch {
                details = [];
              }
            }
            if (!Array.isArray(details)) details = [];

            const totalItemsCount = details.length;
            const totalQuantity = details.reduce(
              (sum, d) => sum + (Number(d.qty) || 0),
              0,
            );

            const allItemsSummary = details
              .map((d) => {
                const itmDoc = d.item || {};
                const name = itmDoc.name
                  ? normalizeText(itmDoc.name.replace(/['"]+/g, ""))
                  : d.name || "Unknown Item";
                const q = Number(d.qty) || 1;
                let extText = "";
                if (Array.isArray(d.extras) && d.extras.length > 0) {
                  const extraNames = d.extras
                    .map((ext) => {
                      if (
                        Array.isArray(ext.extra_id) &&
                        ext.extra_id[0]?.name
                      ) {
                        return normalizeText(ext.extra_id[0].name);
                      }
                      if (ext.extra_id?.name) {
                        return normalizeText(ext.extra_id.name);
                      }
                      if (ext.name) return normalizeText(ext.name);
                      return null;
                    })
                    .filter(Boolean);
                  if (extraNames.length > 0) {
                    extText = ` (${extraNames.join(", ")})`;
                  }
                }
                return `${q} x ${name}${extText}`;
              })
              .join("; ");

            if (!isItemMode) {
              // Export by Order: exactly 1 row per order
              rows.push({
                ...baseRow,
                total_items_count: totalItemsCount,
                total_quantity: totalQuantity,
                items: allItemsSummary || "-",
              });
            } else {
              // Export by Item: line-item breakdown
              if (details.length === 0) {
                rows.push({
                  ...baseRow,
                  item_name: "-",
                  item_code: "-",
                  item_extras: "-",
                  qty: 0,
                  unit_price: 0,
                  regular_price: 0,
                  line_amount: 0,
                  item_discount: 0,
                  items: "-",
                });
              } else {
                details.forEach((detail) => {
                  const itemDoc = detail.item || {};
                  const itemName = itemDoc.name
                    ? normalizeText(itemDoc.name.replace(/['"]+/g, ""))
                    : detail.name || "Unknown Item";
                  const itemCode = itemDoc.item_code || detail.item_code || "-";
                  const qty = Number(detail.qty) || 0;
                  const unitPrice =
                    detail.price !== undefined && detail.price !== null
                      ? Number(detail.price)
                      : 0;
                  const regularPrice =
                    detail.regular_price !== undefined &&
                    detail.regular_price !== null
                      ? Number(detail.regular_price)
                      : unitPrice;
                  const lineAmount = unitPrice * qty;
                  const itemDiscount = Math.max(
                    0,
                    (regularPrice - unitPrice) * qty,
                  );

                  let extrasText = "";
                  if (
                    Array.isArray(detail.extras) &&
                    detail.extras.length > 0
                  ) {
                    const extraNames = detail.extras
                      .map((ext) => {
                        if (
                          Array.isArray(ext.extra_id) &&
                          ext.extra_id[0]?.name
                        ) {
                          return normalizeText(ext.extra_id[0].name);
                        }
                        if (ext.extra_id?.name) {
                          return normalizeText(ext.extra_id.name);
                        }
                        if (ext.name) return normalizeText(ext.name);
                        return null;
                      })
                      .filter(Boolean);

                    if (extraNames.length > 0) {
                      extrasText = extraNames.join(", ");
                    }
                  }

                  rows.push({
                    ...baseRow,
                    item_name: itemName,
                    item_code: itemCode,
                    item_extras: extrasText || "-",
                    qty,
                    unit_price: unitPrice,
                    regular_price: regularPrice,
                    line_amount: lineAmount,
                    item_discount: itemDiscount,
                    items:
                      allItemsSummary ||
                      `${qty} x ${itemName}${
                        extrasText ? ` (${extrasText})` : ""
                      }`,
                  });
                });
              }
            }
          });

          this.setState(
            {
              data: rows,
              headers: activeHeaders,
              activeFilename,
              isForExport: false,
              data_status: "fetched",
            },
            () => {
              setTimeout(() => {
                if (window.ReactNativeWebView) {
                  let dataPayload = JSON.stringify({
                    type: "dl_csv",
                    fileName: `${new Date().getTime()}_${activeFilename}`,
                    headers: activeHeaders,
                    rowData: this.state.data,
                  });
                  window.ReactNativeWebView.postMessage(dataPayload);
                  return null;
                }

                this.csvLinkEl.current.link.click();
              });
            },
          );

          this.handleOnDataFetched();
        })
        .catch((error) => {
          this._isMounted &&
            this.setState({ error: error, showSwalError: true });
          this.handleOnDataFetchError();
        });
  }

  render() {
    const {
      data = [],
      data_status = "",
      headers,
      activeFilename,
      activeMode = "order",
    } = this.state;
    const { filename = "", headers: propHeaders } = this.props;

    const isLoading =
      data_status !== "fetching" && data_status !== "fetched"
        ? false
        : data_status === "fetching";
    const isSpinnerDisplayed = isLoading && !this.initialLoad;

    if (propHeaders) {
      return (
        <div className="d-flex align-items-center flex-nowrap flex-shrink-0">
          <button
            type="button"
            className="btn btn-light py-2 px-3 ml-2 text-nowrap d-inline-flex align-items-center flex-shrink-0"
            disabled={isSpinnerDisplayed}
            onClick={(e) => this.getCSVData(e, "order")}
          >
            <span className="d-inline-flex align-items-center text-nowrap position-relative">
              <span className={`${isSpinnerDisplayed ? "pr-8" : ""}`}>
                Export to CSV
                {isSpinnerDisplayed ? (
                  <span className="spinner spinner-sm spinner-left spinner-primary" />
                ) : (
                  <span className="ml-2">
                    <i className="fas fa-file-export text-primary" />
                  </span>
                )}
              </span>
            </span>
          </button>

          <CSVLink
            filename={activeFilename || filename}
            ref={this.csvLinkEl}
            data={data}
            headers={headers || propHeaders}
          />
        </div>
      );
    }

    return (
      <div className="d-flex align-items-center flex-nowrap flex-shrink-0">
        <button
          type="button"
          className="btn btn-light py-2 px-3 ml-2 text-nowrap d-inline-flex align-items-center flex-shrink-0"
          disabled={isSpinnerDisplayed}
          onClick={(e) => this.getCSVData(e, "order")}
        >
          <span className="d-inline-flex align-items-center text-nowrap position-relative">
            <span
              className={`${
                isSpinnerDisplayed && activeMode === "order" ? "pr-8" : ""
              }`}
            >
              Export by Order
              {isSpinnerDisplayed && activeMode === "order" ? (
                <span className="spinner spinner-sm spinner-left spinner-primary" />
              ) : (
                <span className="ml-2">
                  <i className="fas fa-file-invoice text-primary" />
                </span>
              )}
            </span>
          </span>
        </button>

        <button
          type="button"
          className="btn btn-light py-2 px-3 ml-2 text-nowrap d-inline-flex align-items-center flex-shrink-0"
          disabled={isSpinnerDisplayed}
          onClick={(e) => this.getCSVData(e, "item")}
        >
          <span className="d-inline-flex align-items-center text-nowrap position-relative">
            <span
              className={`${
                isSpinnerDisplayed && activeMode === "item" ? "pr-8" : ""
              }`}
            >
              Export by Item
              {isSpinnerDisplayed && activeMode === "item" ? (
                <span className="spinner spinner-sm spinner-left spinner-primary" />
              ) : (
                <span className="ml-2">
                  <i className="fas fa-boxes text-info" />
                </span>
              )}
            </span>
          </span>
        </button>

        <CSVLink
          filename={activeFilename || filename}
          ref={this.csvLinkEl}
          data={data}
          headers={headers || this.csvHeaders}
        />
      </div>
    );
  }
}

export default ExportCSV;
